import { BetData } from "@fresh8/types/src/betData";
import { AdTag, AdTagCampaign, Operator } from "@fresh8/types/src";
import { preview, publisherBet } from "../prefetch";
import { Ref, ref, watchEffect } from "vue";

/**
 * Async function
 */
async function awaitPromise<T>(promise: Promise<T>) {
  let result: T | undefined;
  let loading = true;
  let error = false;

  try {
    const res = await promise;
    result = res;
    loading = false;
  } catch (e) {
    loading = false;
    error = true;
  }

  return { result, loading, error };
}

/**
 * Synchronous function that returns a ref objects.
 * After the promise is fulfilled, it changes the ref.
 */
function awaitPromiseEffect<T>(promise: Promise<T>) {
  const result = ref<T | undefined>();
  const loading = ref(true);
  const error = ref(false);

  watchEffect(async () => {
    try {
      const res = await promise;
      result.value = res;
      loading.value = false;
    } catch (e) {
      loading.value = false;
      error.value = true;
    }
  });

  return { result, loading, error };
}

/** Fetches the bet data from the publisher recomendor for the given operator */
export function useGetPublisherBetData(
  adTag: Ref<AdTag>,
  operator: Ref<Operator | undefined>,
  campaign: Ref<AdTagCampaign | undefined>
): {
  result: Ref<BetData | undefined>;
  loading: Ref<boolean>;
  error: Ref<boolean>;
} {
  const addr = new URL(window.location.href);
  const usePreview = addr.searchParams.get("preview");

  const betDataPromise = awaitPromiseEffect<BetData>(
    usePreview
      ? preview(adTag.value.operators[0].key)
      : publisherBet(adTag, campaign, operator)
  );

  return betDataPromise;
}

export async function useAsyncGetPublisherBetData(
  adTag: Ref<AdTag | undefined>,
  operator: Ref<Operator | undefined>,
  campaign: Ref<AdTagCampaign | undefined>
): Promise<{
  result?: BetData;
  loading: boolean;
  error: boolean;
}> {
  const addr = new URL(window.location.href);
  const usePreview = addr.searchParams.get("preview");

  const result = await awaitPromise<BetData>(
    usePreview
      ? preview(adTag.value?.operators[0].key)
      : publisherBet(adTag, campaign, operator)
  );
  return result;
}
